/**
 * Per-panel numbers of the design being edited: the server's yield for panels that have not moved, and for new or
 * moved ones an estimate from the flux grid under them (the same Google flux the layout stage samples). Solar
 * access and TSRF are the mean of the analysis grid under each panel (the backend's shade report uses the same
 * rule). Pure: unit tested.
 */
import type { S } from "../../api/client";
import type { Vec2 } from "../geo.ts";
import type { PanelItem } from "../model.ts";
import { meanInPolygon, type Raster } from "./grid.ts";

export interface PanelYield {
  key: string;
  face_id: string;
  plan: Vec2[];
  /** kWh per year, raw DC (before system losses), as the layout stage reports it. */
  kwh: number;
  access: number;
  tsrf: number;
  /** "design": the saved layout's value; "estimate": sampled here for a new or moved panel. */
  source: "design" | "estimate";
}

const same = (a: number, b: number, tol = 1e-3) => Math.abs(a - b) <= tol;

/** Whether a draft panel still is the saved one (same face, place, rotation and orientation). */
export function unchanged(p: PanelItem, saved: S["LayoutPanel"] | undefined): boolean {
  if (!saved || saved.face_id !== p.face_id || saved.orientation !== p.orientation) return false;
  const [u, v] = saved.center_uv as [number, number];
  return same(u, p.center[0]) && same(v, p.center[1]) && same(saved.rotation_deg ?? 0, p.rotation, 0.01);
}

export function panelYields(
  panels: { item: PanelItem; plan: Vec2[] }[],
  saved: S["LayoutPanel"][],
  moduleWatts: number,
  layers: { annual: Raster | null; access: Raster | null; tsrf: Raster | null },
): PanelYield[] {
  const byId = new Map(saved.map((p) => [p.id, p]));
  return panels.map(({ item, plan }) => {
    const original = item.id ? byId.get(item.id) : undefined;
    const keep = unchanged(item, original);
    const kwh = keep ? original!.annual_kwh : layers.annual ? meanInPolygon(layers.annual, plan) * (moduleWatts / 1000) : NaN;
    return {
      key: item.key,
      face_id: item.face_id,
      plan,
      kwh,
      access: layers.access ? meanInPolygon(layers.access, plan) : NaN,
      tsrf: layers.tsrf ? meanInPolygon(layers.tsrf, plan) : NaN,
      source: keep ? "design" : "estimate",
    };
  });
}

export interface FaceSummary {
  face_id: string;
  panels: number;
  kwh: number;
  access: number;
  tsrf: number;
}

/** Totals per face of the panels on it (means weighted by panel). */
export function faceSummaries(yields: PanelYield[]): Map<string, FaceSummary> {
  const out = new Map<string, FaceSummary & { n: number; na: number; nt: number }>();
  for (const y of yields) {
    const f = out.get(y.face_id) ?? { face_id: y.face_id, panels: 0, kwh: 0, access: 0, tsrf: 0, n: 0, na: 0, nt: 0 };
    f.panels++;
    if (Number.isFinite(y.kwh)) f.kwh += y.kwh;
    if (Number.isFinite(y.access)) {
      f.access += y.access;
      f.na++;
    }
    if (Number.isFinite(y.tsrf)) {
      f.tsrf += y.tsrf;
      f.nt++;
    }
    out.set(y.face_id, f);
  }
  return new Map(
    [...out].map(([id, f]) => [id, { face_id: id, panels: f.panels, kwh: f.kwh, access: f.na ? f.access / f.na : NaN, tsrf: f.nt ? f.tsrf / f.nt : NaN }]),
  );
}
